import { Directive, ElementRef, OnDestroy, Renderer2 } from '@angular/core';
import { get, isString } from 'lodash-es';

import { toInt } from 'src/app/shared/utilities/number.utils';
import { validString } from 'src/app/shared/utilities/types.utils';

@Directive({
	selector: '[ahpTransformMonthYear]',
})
export class TransformMonthYearDirective implements OnDestroy {
	private destroyListeners: any[] = [];

	constructor(private el: ElementRef, private renderer: Renderer2) {
		const { nativeElement } = this.el;
		this.renderer.setAttribute(nativeElement, 'ahpTransformMonthYear', '');

		this.destroyListeners = [
			this.renderer.listen(nativeElement, 'change', (evt) => {
				reformatValue(evt);
			}),
			this.renderer.listen(nativeElement, 'input', (evt) => {
				reformatValue(evt);
			}),
			this.renderer.listen(nativeElement, 'keydown', (evt) => {
				formatBackExpiry(evt);
			}),
			this.renderer.listen(nativeElement, 'keypress', (evt) => {
				restrictNumeric(evt);
				restrictExpiry(evt);
				formatCardExpiry(evt);
				formatForwardSlashAndSpace(evt);
				formatForwardExpiry(evt);
			}),
		];
	}

	ngOnDestroy() {
		this.destroyListeners.forEach((destroy) => destroy());
	}
}

/**
 * Sets the value and triggers an event so that the model gets updated
 * @param evt The event
 * @param nativeElement The native element
 * @param value The value to set
 */
function setValue(evt: Event, nativeElement: HTMLInputElement, value: string): void {
	evt.preventDefault();
	nativeElement.value = value;
	const event = document.createEvent('Event');
	event.initEvent(evt.type, false, false);
	nativeElement.dispatchEvent(event);
}

/**
 * formats the card expiry string.
 * @param expiration The main string
 * @param yearLength The number of characters the year can be
 */
function formatCardExpiryString(expiration: string, yearLength: number = 2) {
	const expiry = replaceFullWidthCharacters(expiration);
	const parts = expiry.match(/^\D*(\d{1,2})(\D+)?(\d{1,4})?/);
	if (parts === null) {
		return '';
	}

	let [, mon = '', sep = '', year = ''] = parts;

	// control browser autofill
	if (yearLength === 2) {
		year = year.slice(-2);
	}

	if (toInt(mon) > 12) {
		mon = '12';
	}

	if (year.length > 0) {
		sep = '/';
	} else if (sep === '/') {
		mon = mon.substring(0, 1);
		sep = '';
	} else if (mon.length === 2 || sep.length > 0) {
		sep = '/';
	} else if (mon.length === 1 && mon !== '0' && mon !== '1') {
		mon = `0${mon}`;
		sep = '/';
	}
	return `${mon}${sep}${year}`;
}

/**
 * Format the string when user types the backspace button
 * @param evt The keyboard event
 */
function formatBackExpiry(evt: KeyboardEvent): void {
	if (evt.which !== 8) {
		return;
	}

	const target = evt.target as HTMLInputElement;
	const { value } = target;
	const cursor = getCaretPosition(target);

	if (cursor && cursor !== value.length) {
		return;
	}
	if (/\d\s\/\s$/.test(value)) {
		setValue(evt, target, value.replace(/\d\s\/\s$/, ''));
	}
}

/**
 * Restricts to only numeric characters being entered
 * @param evt THe keybaord event
 */
function restrictNumeric(evt: KeyboardEvent): void {
	if (evt.metaKey || evt.ctrlKey || evt.which < 33) {
		return;
	}

	const input = String.fromCharCode(evt.which);

	if (!/^\d+$/.test(input)) {
		evt.preventDefault();
	}
}

/**
 * Formats the year to always be whatever you want
 * @param evt The keyboard event
 * @param [yearLength=2] The length of your year
 */
function restrictExpiry(evt: KeyboardEvent, yearLength: number = 2): void {
	const digit = String.fromCharCode(evt.which);
	const target = evt.target as HTMLInputElement;
	let { value } = target;

	if (!/^\d+$/.test(digit) || hasTextSelected(target)) {
		return;
	}

	value = value.replace(/\D/g, '');

	if (value.length > yearLength + 2) {
		evt.preventDefault();
	}
}

/**
 * Formats the expiry string
 * @param evt The keyboard event
 */
function formatCardExpiry(evt: KeyboardEvent): void {
	const digit = String.fromCharCode(evt.which);
	if (!/^\d+$/.test(digit)) {
		return;
	}

	const target = evt.target as HTMLInputElement;
	const val = `${target.value}${digit}`;

	if (/^\d$/.test(val) && val !== '0' && val !== '1') {
		setValue(evt, target, `0${val}/`);
	} else if (/^\d\d$/.test(val)) {
		setValue(evt, target, `${val}/`);
	}
}

/**
 * If someone types the forward slash or space themselves
 * @param evt The keyboard event
 */
function formatForwardSlashAndSpace(evt: KeyboardEvent): void {
	const target = evt.target as HTMLInputElement;
	const which = String.fromCharCode(evt.which);
	const { value } = target;

	if (!(which === '/' || which === ' ')) {
		return;
	}

	if (/^\d$/.test(value) && value !== '0') {
		target.value = `0${value}/`;
	}
}

/**
 * Adds the forward slash
 * @param evt The keyboard event
 */
function formatForwardExpiry(evt: KeyboardEvent): void {
	const target = evt.target as HTMLInputElement;
	const digit = String.fromCharCode(evt.which);
	if (!/^\d+$/.test(digit)) {
		return;
	}
	const val = target.value;
	if (/^\d\d$/.test(val)) {
		target.value = `${val}/`;
	}
}

/**
 * Reformats the expiration input
 * @param evt The input event
 * @param [yearLength=2] The length you want for the year
 */
function reformatValue(evt: Event, yearLength: number = 2): void {
	let cursor;
	const target = evt.target as HTMLInputElement;
	if (target.value === '') {
		return;
	}

	target.value = formatCardExpiryString(removeNonNumberOrSlash(target.value), yearLength);
	cursor = getCaretPosition(target);

	if (cursor != null && evt.type !== 'change') {
		return target.setSelectionRange(cursor, cursor);
	}
}

/**
 * Removes any non number or forward slash characters from a string.
 * @param value The value to replace
 */
function removeNonNumberOrSlash(value: string): string {
	if (!validString(value)) {
		return value;
	}

	return value.replace(/[^\d/]*/g, '');
}

/**
 * Get the cart position when a user is selecting text
 * @param ele The input element to get the cart position on selection
 */
function getCaretPosition(el: HTMLInputElement): number | undefined {
	if (el.selectionStart != null) {
		return el.selectionStart;
	} else if ((document as any).selection != null) {
		el.focus();

		const range = (document as any).selection.createRange();
		// @ts-ignore
		const textRange = el.createTextRange();
		const textRangeDuplicate = textRange.duplicate();

		textRange.moveToBookmark(range.getBookmark());
		textRangeDuplicate.setEndPoint('EndToStart', textRange);
		return textRangeDuplicate.text.length;
	}

	return;
}

/**
 * We have to replace the full width characters with half width versions.
 * @see {@link https://en.wikipedia.org/wiki/Halfwidth_and_Fullwidth_Forms_(Unicode_block)}
 * @param val The value to replace
 */
function replaceFullWidthCharacters(val): string {
	const str = isString(val) ? val : '';
	const fullWidth = '\uff10\uff11\uff12\uff13\uff14\uff15\uff16\uff17\uff18\uff19';
	const halfWidth = '0123456789';
	const chars = str.split('');
	const len = chars.length;
	let value = '';

	for (let i = 0; i < len; i += 1) {
		let char = chars[i];
		const index = fullWidth.indexOf(char);

		if (index > -1) {
			char = halfWidth[index];
		}

		value += char;
	}

	return value;
}

/**
 * Find out if the target element has text selected
 * @param target The target element
 */
function hasTextSelected(target: HTMLInputElement): boolean {
	if (get(document, 'selection.createRange') && (document as any).selection.createRange().text) {
		return true;
	}
	return target.selectionStart != null && target.selectionStart !== target.selectionEnd;
}
